/**
 * The connector between Compare to Thrive and the write action.
 *
 * ```
 *   ┌────────────────────┐  ▶  ┌──────────────────┐
 *   │ Compare to Thrive  │     │  Add to search   │
 *   └────────────────────┘     └──────────────────┘
 * ```
 *
 * It says compare-then-add WITHOUT disabling anything — going straight to the
 * write action is a legitimate workflow, so the order has to be a hint to the
 * eye rather than a gate.
 *
 * ## Why this replaced the chevron step-flow (2026-08-27)
 *
 * The predecessor (`ActionSequence`) made the same claim with more conviction:
 * the two controls were CUT so the first pointed into the second's notch. The
 * shape read. The buttons did not — and that was not a tuning problem, it was
 * structural. `clip-path` clips the BORDER and the box-shadow along with the
 * paint, so neither step could carry the border or the elevation that spells
 * "pressable" in this design system; what was left was a 15% teal fill, which
 * is the same vocabulary as a chip and one shade away from `disabled`. The
 * wrapper's `overflow-hidden` (needed for the rounded outer ends, because
 * `clip-path` and `border-radius` cannot coexist on one element) also ate any
 * outset focus ring, so keyboard focus rendered as three sides and a diagonal.
 *
 * Moving the arrow OUT of the controls buys all of that back at once: both
 * actions are the plain `@nu-assist/ui` `Button`, with its real border, its
 * real `rounded-md`, and its real closed focus ring. The order hint is quieter
 * than a notch and that is the accepted trade — it is paid for by two controls
 * that read as controls.
 *
 * This is NOT the `›` glyph the chevron design was written to replace. A glyph
 * is a character in the accessibility tree that a screen reader has to decide
 * what to do with; this is a shape with no text content at all, marked
 * `aria-hidden`. The reading order is already carried by the DOM order of the
 * two buttons.
 *
 * The triangle is the classic zero-box border trick: a 0×0 element whose left
 * border is the only opaque one, so the two transparent neighbours cut it into
 * a point. Arbitrary values carry the GEOMETRY only — the colour is a token.
 * That is the same latitude the deleted file took with `[clip-path:polygon(…)]`,
 * and it is not the `text-[Npx]` sizing the design system forbids.
 *
 * `accent-ink`, and specifically NOT `border-hover`, which an earlier version
 * used. Two reasons, one semantic and one that would have shown on screen:
 * `--border-hover` is a HOVER token, and a resting decoration borrowing one
 * reads as intentional to the next person and gets copied; and it is
 * `rgba(0, 229, 204, 0.3)`, so the entire order hint — the only thing carrying
 * compare-then-add now that the shapes are gone — would live in an 8px mark at
 * 30% alpha. `-ink` is the accent-as-foreground tier: full teal on dark,
 * deepened on light, so the arrow holds in both themes at the size it is.
 */
export function ActionArrow() {
  return (
    <span
      aria-hidden="true"
      data-testid="action-arrow"
      className="h-0 w-0 shrink-0 border-y-[6px] border-l-[8px] border-y-transparent border-l-accent-ink"
    />
  );
}
